import { captureException } from '@sentry/node';
import Router from 'next/router';
import React, { ReactElement, useEffect } from 'react';
import Fallback from './Fallback';
import SentryErrorBoundary from './SentryErrorBoundary';

export interface ErrorBoundaryProps {
  children: ReactElement;
}

const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  useEffect(() => {
    const onRouteChangeError = (error: any, url: any) => {
      let path = 'unknown';

      // Handle route cancellation (intentional behavior)
      if (error.cancelled) {
        // Don't log this as a warning since it's intentional behavior
        return;
      }

      // Extract path information
      if (typeof url === 'string') {
        path = url;
      } else if (typeof url.pathname === 'string') {
        path = url.pathname;
      } else if (url && typeof url === 'object') {
        // Try to extract path from various possible formats
        path = url.as || url.href || url.pathname || 'unknown';
      }
      const modifiedError = new Error(
        `Route change error at path [${path}]: ${error.message}`
      );
      modifiedError.stack = error.stack;
      modifiedError.name = error.name;
      captureException(modifiedError);
    };

    Router.events.on('routeChangeError', onRouteChangeError);
    return () => {
      Router.events.off('routeChangeError', onRouteChangeError);
    };
  }, []);

  return (
    <SentryErrorBoundary fallback={Fallback}>{children}</SentryErrorBoundary>
  );
};

export default ErrorBoundary;
